import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { extractAndGeocodeLocations } from '@/lib/locationExtractor';
// Note: DM sound + toast + OS notification are owned by
// `useGlobalNotificationAlerts`, which listens on the `notifications`
// table. The DB trigger `trg_notify_new_message` inserts a `dm_received`
// row for every incoming DM, so this context no longer emits alerts
// itself (would double-fire).


export interface Message {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
  isMe: boolean;
  /** ISO timestamp when the recipient read this message; null while unread. */
  read_at?: string | null;
}

export interface ChatConversation {
  id: string;
  /** Display name of the counterpart. */
  name: string;
  /**
   * Storage path in the private `avatars` bucket for the counterpart's photo,
   * or null when they have not set one. UI must pass this to `UserAvatar`,
   * which signs it and falls back to initials. Never a public URL or
   * placeholder string — those break signed-URL minting.
   */
  avatar: string | null;
  isOnline: boolean;
  lastMessage?: string;
  messages: Message[];
  isMinimized: boolean;
  userId?: string;
  loadId?: string;
  unreadCount: number;
  isAtlas?: boolean;
}

interface ChatContextType {
  conversations: ChatConversation[];
  openChats: string[];
  hiddenConversations: string[];
  notification: {
    id: string;
    name: string;
    avatar: string;
    message: string;
  } | null;
  atlasConversation: ChatConversation | null;
  atlasOpenSignal: number;
  atlasCreditsExhausted: boolean;
  atlasDockOpen: boolean;
  
  messengerOpen: boolean;
  unreadTotal: number;

  // Actions
  openChatWithUser: (userId: string, userName: string, userAvatar?: string, loadId?: string) => void;
  toggleChat: (conversationId: string) => void;
  closeChat: (conversationId: string) => void;
  hideConversation: (conversationId: string) => void;
  sendMessage: (conversationId: string, text: string) => void;
  openAtlasChat: () => void;
  forceOpenAtlas: () => void;
  sendAtlasMessage: (text: string) => void;
  addAtlasMessage: (content: string, isFromUser?: boolean) => void;
  clearAtlasLoadingMessages: () => void;
  openAtlasDock: () => void;
  closeAtlasDock: () => void;
  setNotification: (notification: any) => void;
  setOpenChats: React.Dispatch<React.SetStateAction<string[]>>;
  setConversations: React.Dispatch<React.SetStateAction<ChatConversation[]>>;
  setHiddenConversations: React.Dispatch<React.SetStateAction<string[]>>;
  toggleMessenger: () => void;
  openMessenger: () => void;
  closeMessenger: () => void;
  openConversationFromNotification: (conversationId: string) => void;
}

const NOOP = () => {};
const NOOP_ASYNC = async () => {};
const FALLBACK_CONTEXT: ChatContextType = {
  conversations: [],
  openChats: [],
  hiddenConversations: [],
  notification: null,
  atlasConversation: null,
  atlasOpenSignal: 0,
  atlasCreditsExhausted: false,
  atlasDockOpen: false,
  openAtlasDock: NOOP,
  closeAtlasDock: NOOP,
  openChatWithUser: NOOP_ASYNC as any,
  toggleChat: NOOP_ASYNC as any,
  closeChat: NOOP,
  hideConversation: NOOP,
  sendMessage: NOOP_ASYNC as any,
  openAtlasChat: NOOP,
  forceOpenAtlas: NOOP,
  sendAtlasMessage: NOOP_ASYNC as any,
  addAtlasMessage: NOOP,
  clearAtlasLoadingMessages: NOOP,
  setNotification: NOOP,
  setOpenChats: NOOP as any,
  setConversations: NOOP as any,
  setHiddenConversations: NOOP as any,
  messengerOpen: false,
  unreadTotal: 0,
  toggleMessenger: NOOP,
  openMessenger: NOOP,
  closeMessenger: NOOP,
  openConversationFromNotification: NOOP,
};

const ChatContext = createContext<ChatContextType>(FALLBACK_CONTEXT);

const MAX_ATLAS_MESSAGES = 20;
function trimAtlasMessages(msgs: Message[]): Message[] {
  if (msgs.length <= MAX_ATLAS_MESSAGES) return msgs;
  const hasWelcome = msgs[0]?.id === 'welcome-msg';
  if (hasWelcome) {
    return [msgs[0], ...msgs.slice(-(MAX_ATLAS_MESSAGES - 1))];
  }
  return msgs.slice(-MAX_ATLAS_MESSAGES);
}

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [hiddenConversations, setHiddenConversations] = useState<string[]>([]);
  const [openChats, setOpenChats] = useState<string[]>([]);
  const [notification, setNotification] = useState<{
    id: string;
    name: string;
    avatar: string;
    message: string;
  } | null>(null);
  const [atlasConversation, setAtlasConversation] = useState<ChatConversation | null>(null);
  const [atlasOpenSignal, setAtlasOpenSignal] = useState(0);
  const [atlasCreditsExhausted, setAtlasCreditsExhausted] = useState(false);
  const [atlasDockOpen, setAtlasDockOpen] = useState(false);
  const [messengerOpen, setMessengerOpen] = useState(false);
  
  // Add caching for user profiles and conversations
  const profileCache = useRef<Map<string, any>>(new Map());
  const conversationCache = useRef<Map<string, string>>(new Map()); // userId -> conversationId
  
  // Track open chats with ref to avoid subscription re-creation
  const openChatsRef = useRef<string[]>([]);
  
  // Track processed message IDs to prevent duplicate notifications
  const processedMessagesRef = useRef<Set<string>>(new Set());

  // Ref invoked when the user clicks an OS-level Web Notification.
  // Populated below once toggleChat is defined so the click handler can
  // open the conversation without stale closures.
  const openConversationFromNotifRef = useRef<((conversationId: string) => void) | null>(null);

  // P2: messages typed while a temp conversation is being swapped to a real id
  // get queued here keyed by tempConversationId, and flushed once the swap lands.
  const pendingMessagesRef = useRef<Map<string, string[]>>(new Map());

  const { user, loading, profile } = useAuth();
  const userId = user?.id;

  // Fetch full conversation list from DB (source of truth).
  const fetchConversations = React.useCallback(async () => {
    if (!userId) return;
    try {
      const { data: conversationsData, error } = await supabase
        .from('conversations' as any)
        .select(`
          id,
          participant_1_id,
          participant_2_id,
          load_id,
          last_activity_at
        `)
        .or(`participant_1_id.eq.${userId},participant_2_id.eq.${userId}`)
        .order('last_activity_at', { ascending: false });

      if (error) throw error;

      const conversationIds = conversationsData?.map((c: any) => c.id) || [];
      let messagesData: any[] = [];

      if (conversationIds.length > 0) {
        const { data: messages } = await supabase
          .from('messages' as any)
          .select('*')
          .in('conversation_id', conversationIds)
          .order('created_at', { ascending: true });

        messagesData = messages || [];
      }

      const userIds = new Set<string>();
      conversationsData?.forEach((conv: any) => {
        userIds.add(conv.participant_1_id);
        userIds.add(conv.participant_2_id);
      });

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url, user_type')
        .in('id', Array.from(userIds));

      // Brokers see shippers as "Shipper" until they've won a load from that shipper.
      const viewerIsShipper = profile?.user_type === 'shipper';
      const shipperIds = viewerIsShipper ? [] : (profiles || []).filter((p: any) => p.id !== userId && p.user_type === 'shipper').map((p: any) => p.id);
      let revealedShippers = new Set<string>();
      if (shipperIds.length > 0) {
        const { data: revealed } = await (supabase.rpc as any)('chat_revealed_shipper_ids', { _shipper_ids: shipperIds });
        revealedShippers = new Set((revealed as string[] | null) ?? []);
      }

      const chatConversations: ChatConversation[] = conversationsData?.map((conv: any) => {
        const otherUserId = conv.participant_1_id === userId ? conv.participant_2_id : conv.participant_1_id;
        const otherUserProfile = profiles?.find((p: any) => p.id === otherUserId);
        const masked = !viewerIsShipper && otherUserProfile?.user_type === 'shipper' && !revealedShippers.has(otherUserId);

        const userName = masked ? 'Shipper' : otherUserProfile
          ? `${otherUserProfile.first_name || ''} ${otherUserProfile.last_name || ''}`.trim() || `User ${otherUserId.slice(-4)}`
          : `User ${otherUserId.slice(-4)}`;

        const conversationMessages = messagesData.filter((msg: any) => msg.conversation_id === conv.id);
        const messages: Message[] = conversationMessages.map((msg: any) => {
          processedMessagesRef.current.add(msg.id);
          return {
            id: msg.id,
            sender_id: msg.sender_id,
            content: msg.content,
            created_at: msg.created_at,
            isMe: msg.sender_id === userId,
            read_at: msg.read_at ?? null,
          };
        });

        const unreadCount = conversationMessages.filter((msg: any) =>
          msg.sender_id !== userId && !msg.read_at
        ).length;

        return {
          id: conv.id,
          name: userName,
          avatar: masked ? null : (otherUserProfile?.avatar_url ?? null),
          isOnline: false,
          lastMessage: messages[messages.length - 1]?.content,
          messages,
          isMinimized: false,
          userId: otherUserId,
          loadId: conv.load_id,
          unreadCount,
        };
      }) || [];

      // Merge: replace all fields for known conversations from DB, but preserve
      // any local-only temp conversations (id starts with 'temp-').
      setConversations(prev => {
        const temps = prev.filter(c => c.id.startsWith('temp-'));
        return [...chatConversations, ...temps];
      });
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [userId]);

  // Fetch a single conversation's messages from DB. Standard chat-app pattern:
  // DB is source of truth. Called on open + on focus/visibility regain so no
  // missed realtime event can ever hide a message that's already in Postgres.
  const fetchConversationMessages = React.useCallback(async (conversationId: string) => {
    if (!userId || !conversationId || conversationId.startsWith('temp-')) return;
    try {
      const { data, error } = await supabase
        .from('messages' as any)
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const rows = (data as any[]) || [];
      const messages: Message[] = rows.map(msg => {
        processedMessagesRef.current.add(msg.id);
        return {
          id: msg.id,
          sender_id: msg.sender_id,
          content: msg.content,
          created_at: msg.created_at,
          isMe: msg.sender_id === userId,
          read_at: msg.read_at ?? null,
        };
      });

      const isOpen = openChatsRef.current.includes(conversationId);
      const unreadIds = rows
        .filter(m => m.sender_id !== userId && !m.read_at)
        .map(m => m.id);

      setConversations(prev => prev.map(conv => {
        if (conv.id !== conversationId) return conv;
        return {
          ...conv,
          messages,
          lastMessage: messages[messages.length - 1]?.content ?? conv.lastMessage,
          unreadCount: isOpen ? 0 : unreadIds.length,
        };
      }));

      // If the chat is open, mark newly-fetched unread as read in DB.
      if (isOpen && unreadIds.length > 0) {
        await supabase
          .from('messages')
          .update({ read_at: new Date().toISOString() })
          .in('id', unreadIds);
      }
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
    }
  }, [userId]);

  // Initial load of conversations.
  useEffect(() => {
    if (!userId || loading) return;
    fetchConversations();
  }, [userId, loading, fetchConversations]);

  // Refetch on tab focus / visibility regain. Debounced 500ms so rapid
  // focus/blur cycles (window switch, alt-tab spam) don't hammer Supabase.
  useEffect(() => {
    if (!userId || loading) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        fetchConversations();
        // Also refresh any open chat's transcript so a message sent while
        // this tab was hidden appears within ~1s of return.
        for (const id of openChatsRef.current) {
          fetchConversationMessages(id);
        }
      }, 500);
    };
    const onVis = () => { if (document.visibilityState === 'visible') run(); };
    const onFocus = () => run();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      if (timer) clearTimeout(timer);
    };
  }, [userId, loading, fetchConversations, fetchConversationMessages]);


  // Keep openChatsRef in sync with openChats state
  useEffect(() => {
    openChatsRef.current = openChats;
  }, [openChats]);

  // Set up real-time subscriptions - only depends on userId/loading to prevent re-creation.
  //
  // Self-heal (same pattern as notificationsBus.ts / useRealtimeSelfHeal.ts):
  //  - status callback detects CHANNEL_ERROR / TIMED_OUT / CLOSED and rebuilds
  //    the channel with backoff (iOS kills the socket when the webview is
  //    suspended; without this the channel stayed dead for the whole session).
  //  - SUBSCRIBED triggers a gap-fill refetch so messages that landed while the
  //    channel was down still show up.
  //  - Capacitor appStateChange (resume) reconnects realtime with the freshly
  //    refreshed JWT and rebuilds the channel.
  useEffect(() => {
    if (!userId || loading) return;

    let disposed = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let epoch = 0;
    let gapFillTimer: ReturnType<typeof setTimeout> | null = null;
    let removeAppListener: (() => void) | null = null;

    // Catch up on anything missed while the channel was dead. Debounced so a
    // reconnect storm doesn't hammer Supabase.
    const gapFill = () => {
      if (gapFillTimer || disposed) return;
      gapFillTimer = setTimeout(() => {
        gapFillTimer = null;
        if (disposed) return;
        fetchConversations();
        for (const id of openChatsRef.current) fetchConversationMessages(id);
      }, 300);
    };

    const scheduleRebuild = (reason: string) => {
      if (disposed || retryTimer) return;
      const delay = Math.min(1000 * 2 ** attempt, 15000);
      attempt += 1;
      console.debug('[chat-rt] rebuilding channel', { reason, delay, attempt });
      retryTimer = setTimeout(() => {
        retryTimer = null;
        buildChannel();
      }, delay);
    };

    const buildChannel = () => {
      if (disposed) return;
      if (channel) {
        try { supabase.removeChannel(channel); } catch { /* noop */ }
        channel = null;
      }
      epoch += 1;
      channel = createMessagesChannel(epoch);
    };

    const createMessagesChannel = (gen: number) => supabase
      .channel(`messages-${userId}-${gen}`)

      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          const newMessage = payload.new as any;
          
          // Deduplicate: skip if we've already processed this message
          if (processedMessagesRef.current.has(newMessage.id)) {
            return;
          }
          processedMessagesRef.current.add(newMessage.id);
          
          // Limit the set size to prevent memory leaks
          if (processedMessagesRef.current.size > 1000) {
            const entries = Array.from(processedMessagesRef.current);
            processedMessagesRef.current = new Set(entries.slice(-500));
          }
          
          // Find the conversation this message belongs to
          const { data: conversation } = await supabase
            .from('conversations' as any)
            .select('*')
            .eq('id', newMessage.conversation_id)
            .single();

          if (!conversation) return;

          // Check if this is a conversation the current user participates in
          if ((conversation as any).participant_1_id !== userId && (conversation as any).participant_2_id !== userId) {
            return;
          }

          const conversationId = (conversation as any).id;
          
          // Use ref for current open chats state (avoids stale closure)
          const currentOpenChats = openChatsRef.current;

          // Try to update an existing conversation. If the thread isn't
          // in state yet (brand-new conversation — e.g. a newly-added
          // network broker sending their first-ever DM), we won't match
          // and must seed it via fetchConversations() below so the
          // message appears live without a hard refresh.
          let matchedExisting = false;
          setConversations(prev => {
            if (!prev.some(c => c.id === conversationId)) return prev;
            matchedExisting = true;
            return prev.map(conv => {
            if (conv.id === conversationId) {
              // Check if message already exists to prevent duplicates
              if (conv.messages.some(m => m.id === newMessage.id)) {
                return conv;
              }
              
              const messageToAdd: Message = {
                id: newMessage.id,
                sender_id: newMessage.sender_id,
                content: newMessage.content,
                created_at: newMessage.created_at,
                isMe: newMessage.sender_id === userId,
                read_at: newMessage.read_at ?? null,
              };

              const isUnread = !messageToAdd.isMe;
              const isChatOpen = currentOpenChats.includes(conv.id);

              // If chat is open, mark the message as read immediately
              if (isChatOpen && isUnread) {
                supabase
                  .from('messages')
                  .update({ read_at: new Date().toISOString() })
                  .eq('id', newMessage.id)
                  .then(() => {});
              }

              return {
                ...conv,
                messages: [...conv.messages, messageToAdd],
                lastMessage: newMessage.content,
                unreadCount: isChatOpen ? 0 : (isUnread ? conv.unreadCount + 1 : conv.unreadCount)
              };
            }
            return conv;
            });
          });

          // Unknown thread → refetch the full conversation list so the
          // new conversation is seeded with the counterpart's name +
          // avatar (both come from the profiles join in fetchConversations).
          // processedMessagesRef already includes newMessage.id, and
          // fetchConversations adds every message id it loads, so this
          // cannot double-toast for the same message.
          if (!matchedExisting) {
            await fetchConversations();
          }

          // NOTE: sound, sonner toast, and OS-level web notification are now
          // owned by `useGlobalNotificationAlerts`, which listens on the
          // `notifications` table. The DB trigger `trg_notify_new_message`
          // inserts a `dm_received` row for every incoming DM (see migration
          // 20260728). Firing them here as well would double-alert. The
          // unread-count / message-list / read-receipt state above is chat
          // state — that stays here.



        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          // Sender-side read receipt: recipient stamped read_at -> flip our
          // local message to "Seen". No badge/sound side effects on UPDATE.
          const updated = payload.new as any;
          if (!updated?.id || !updated?.conversation_id) return;
          setConversations(prev => prev.map(conv => {
            if (conv.id !== updated.conversation_id) return conv;
            let changed = false;
            const nextMessages = conv.messages.map(m => {
              if (m.id !== updated.id) return m;
              if (m.read_at === updated.read_at) return m;
              changed = true;
              return { ...m, read_at: updated.read_at ?? null };
            });
            return changed ? { ...conv, messages: nextMessages } : conv;
          }));
        }
      )
      .subscribe((status) => {
        console.debug('[chat-rt] status', status, 'gen', gen);
        if (gen !== epoch || disposed) return;
        if (status === 'SUBSCRIBED') {
          attempt = 0;
          gapFill();
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          scheduleRebuild(status);
        }
      });

    buildChannel();

    // Native (iOS/Android): the OS suspends the webview and the WebSocket dies.
    // On resume, reconnect the socket (with the JWT AuthContext just refreshed)
    // and rebuild the channel, then gap-fill.
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive || disposed) return;
          try {
            // Push the current (possibly refreshed) access token to realtime so a
            // channel that errored on an expired JWT can rejoin.
            supabase.auth.getSession().then(({ data }) => {
              const token = data.session?.access_token;
              if (token) supabase.realtime.setAuth(token);
              try { supabase.realtime.connect(); } catch { /* noop */ }
              buildChannel();
              gapFill();
            });
          } catch { /* noop */ }
        });
        if (disposed) { handle.remove(); return; }
        removeAppListener = () => { handle.remove(); };
      } catch { /* not native / plugin unavailable */ }
    })();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (gapFillTimer) clearTimeout(gapFillTimer);
      if (removeAppListener) removeAppListener();
      if (channel) supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loading]);


  const openChatWithUser = async (userId: string, userName: string, userAvatar?: string, loadId?: string) => {
    if (!user) return;

    // Hoisted so the catch block can reference it for cleanup of queued messages.
    let tempConversationId: string | null = null;

    try {
      // Resolve the correct target user id (defensive: sometimes loadId is mistakenly passed)
      let targetUserId = userId;
      if (loadId && userId === loadId) {
        const { data: loadRec } = await supabase
          .from('loads' as any)
          .select('created_by')
          .eq('id', loadId)
          .maybeSingle();
        const createdBy = (loadRec as any)?.created_by as string | undefined;
        if (createdBy) {
          targetUserId = createdBy;
        }
      }

      // Check cache first for existing conversation
      const cachedConversationId = conversationCache.current.get(targetUserId);
      if (cachedConversationId) {
        const existingConversation = conversations.find(conv => conv.id === cachedConversationId);
        if (existingConversation) {
          if (!openChats.includes(existingConversation.id)) {
            setOpenChats(prev => [...prev, existingConversation.id]);
          }
          return;
        }
      }

      // If conversation already exists locally for this user, just open it
      const existingConversation = conversations.find(conv => conv.userId === targetUserId);
      if (existingConversation) {
        if (!openChats.includes(existingConversation.id)) {
          setOpenChats(prev => [...prev, existingConversation.id]);
        }
        // Cache the conversation ID
        conversationCache.current.set(targetUserId, existingConversation.id);
        return;
      }

      // Optimistic UI: Create temporary conversation immediately
      tempConversationId = `temp-${Date.now()}`;
      const tempConversation: ChatConversation = {
        id: tempConversationId,
        name: userName,
        avatar: userAvatar ?? null,
        isOnline: false,
        lastMessage: undefined,
        messages: [],
        isMinimized: false,
        userId: targetUserId,
        loadId: loadId,
        unreadCount: 0
      };

      // Add temp conversation and open chat immediately
      setConversations(prev => [...prev, tempConversation]);
      setOpenChats(prev => [...prev, tempConversationId]);

      // Now fetch real data in parallel
      const [profilePromise, conversationPromise] = await Promise.allSettled([
        // Check cache first, then fetch if needed
        profileCache.current.has(targetUserId) 
          ? Promise.resolve(profileCache.current.get(targetUserId))
          : supabase
              .from('profiles')
              .select('id, first_name, last_name, avatar_url, user_type')
              .eq('id', targetUserId)
              .maybeSingle()
              .then(result => {
                if (result.data) {
                  profileCache.current.set(targetUserId, result.data);
                }
                return result;
              }),
        
        // Check for existing conversation
        supabase
          .from('conversations' as any)
          .select('*')
          .or(`and(participant_1_id.eq.${user.id},participant_2_id.eq.${targetUserId}),and(participant_1_id.eq.${targetUserId},participant_2_id.eq.${user.id})`)
          .maybeSingle()
      ]);

      const userProfile = profilePromise.status === 'fulfilled' ? profilePromise.value?.data || profilePromise.value : null;
      const existingConv = conversationPromise.status === 'fulfilled' ? conversationPromise.value?.data : null;

      let actualUserName = userProfile
        ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || userName || `User ${targetUserId.slice(-4)}`
        : userName || `User ${targetUserId.slice(-4)}`;
      let actualAvatar = userProfile?.avatar_url ?? userAvatar ?? null;
      // Brokers see shippers as "Shipper" until they've won a load from them.
      if (profile?.user_type !== 'shipper' && userProfile?.user_type === 'shipper') {
        const { data: revealed } = await (supabase.rpc as any)('chat_revealed_shipper_ids', { _shipper_ids: [targetUserId] });
        if (!((revealed as string[] | null) ?? []).includes(targetUserId)) {
          actualUserName = 'Shipper';
          actualAvatar = null;
        }
      }

      let conversationId: string;

      if (existingConv) {
        conversationId = (existingConv as any).id;
      } else {
        // Create new conversation in database
        const { data: newConv, error } = await supabase
          .from('conversations' as any)
          .insert({
            participant_1_id: user.id,
            participant_2_id: targetUserId,
            load_id: loadId
          })
          .select()
          .single();

        if (error) {
          // PERMANENT: surface Postgres error code so RLS (42501) / missing table
          // (42P01) failures are diagnosable in production, not silently swallowed.
          console.error('[chat] conversations INSERT failed', {
            code: (error as any).code,
            message: error.message,
            details: (error as any).details,
            hint: (error as any).hint,
            participant_1_id: user.id,
            participant_2_id: targetUserId,
            load_id: loadId,
          });
          throw error;
        }
        conversationId = (newConv as any).id;
      }

      // Cache the conversation
      conversationCache.current.set(targetUserId, conversationId);

      // Atomically swap temp → real in BOTH conversations + openChats so
      // FloatingChatSystem never sees an openChats id that has no matching
      // conversation (would render null and look like the chat closed).
      // If the real conversation already exists in our list (loaded by
      // fetchConversations on mount), drop the temp and keep the loaded one
      // so message history is preserved.
      flushSync(() => {
        setConversations(prev => {
          const realAlreadyLoaded = prev.some(
            c => c.id === conversationId && c.id !== tempConversationId
          );
          if (realAlreadyLoaded) {
            return prev.filter(c => c.id !== tempConversationId);
          }
          return prev.map(conv =>
            conv.id === tempConversationId
              ? { ...conv, id: conversationId, name: actualUserName, avatar: actualAvatar, isOnline: false }
              : conv
          );
        });
        setOpenChats(prev => {
          const swapped = prev.map(id => id === tempConversationId ? conversationId : id);
          return Array.from(new Set(swapped));
        });
      });

      console.log('Chat opened successfully:', { conversationId, userName: actualUserName });

      // Standard chat-app pattern: DB is source of truth. Always fetch the
      // conversation's messages from DB on open — never rely on realtime alone.
      fetchConversationMessages(conversationId);


      // P2: flush any messages the user typed while the temp→real swap was in flight.
      const queued = pendingMessagesRef.current.get(tempConversationId);
      if (queued && queued.length) {
        pendingMessagesRef.current.delete(tempConversationId);
        for (const t of queued) {
          // sendMessage is in the same closure; safe to call by name.
          await sendMessage(conversationId, t);
        }
      }
    } catch (error: any) {
      // PERMANENT logging — surface Postgres error code (42501 RLS, 42P01 missing
      // table, 23503 FK) so silent-close never masks a backend failure again.
      console.error('[chat] openChatWithUser failed', {
        code: error?.code,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        targetUserId: userId,
        loadId,
      });

      // Drop any messages the user typed against the failed temp conversation.
      if (tempConversationId) {
        pendingMessagesRef.current.delete(tempConversationId);
      }

      // Keep the chat window OPEN with a visible error + retry, instead of the
      // old flushSync that nuked openChats and made every failure look like the
      // window "just closed itself".
      const failedTempId = tempConversationId;
      toast.error("Couldn't open chat", {
        description:
          error?.code === '42501'
            ? 'Permission denied. Please refresh and try again.'
            : error?.code === '42P01'
            ? 'Chat backend unavailable. Please contact support.'
            : (error?.message || 'Please try again.'),
        action: {
          label: 'Retry',
          onClick: () => {
            // Clean up the failed temp shell, then re-attempt.
            if (failedTempId) {
              setConversations(prev => prev.filter(c => c.id !== failedTempId));
              setOpenChats(prev => prev.filter(id => id !== failedTempId));
            }
            openChatWithUser(userId, userName, userAvatar, loadId);
          },
        },
      });
    }
  };

  const toggleChat = async (conversationId: string) => {
    const isOpening = !openChats.includes(conversationId);
    
    setOpenChats(prev => {
      if (prev.includes(conversationId)) {
        return prev.filter(id => id !== conversationId);
      } else {
        return [...prev, conversationId];
      }
    });

    // When opening a chat: fetch messages from DB (source of truth), then
    // mark unread ones read.
    if (isOpening && user) {
      fetchConversationMessages(conversationId);
      try {

        // First update the database
        const { error } = await supabase
          .from('messages')
          .update({ read_at: new Date().toISOString() })
          .eq('conversation_id', conversationId)
          .neq('sender_id', user.id)
          .is('read_at', null);

        if (error) {
          console.error('Error marking messages as read:', error);
        }
        
        // Then reset the local unread count (regardless of DB success for UX)
        setConversations(prevConvs => 
          prevConvs.map(conv => 
            conv.id === conversationId 
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      } catch (error) {
        console.error('Error marking messages as read:', error);
        // Still reset unread count locally for better UX
        setConversations(prevConvs => 
          prevConvs.map(conv => 
            conv.id === conversationId 
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      }
    }
  };

  // Keep the notification-click handler ref current so OS-notification
  // taps open the right conversation with fresh state (no stale closures).
  useEffect(() => {
    openConversationFromNotifRef.current = (conversationId: string) => {
      // Un-minimize if needed.
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, isMinimized: false } : c
      ));
      // Open if closed.
      if (!openChats.includes(conversationId)) {
        void toggleChat(conversationId);
      }
    };
  });

  const closeChat = (conversationId: string) => {
    setOpenChats(prev => prev.filter(id => id !== conversationId));
  };

  const hideConversation = (conversationId: string) => {
    setHiddenConversations(prev => [...prev, conversationId]);
    closeChat(conversationId);
  };

  const sendMessage = async (conversationId: string, text: string) => {
    if (!user) return;

    // P2: queue messages typed before the temp→real conversation id swap lands.
    // openChatWithUser drains this queue immediately after the swap.
    if (conversationId.startsWith('temp-')) {
      const arr = pendingMessagesRef.current.get(conversationId) ?? [];
      arr.push(text);
      pendingMessagesRef.current.set(conversationId, arr);
      return;
    }

    try {
      const { data: inserted, error } = await supabase
        .from('messages' as any)
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: text,
          message_type: 'text'
        })
        .select()
        .single();

      if (error) throw error;

      // Optimistic echo: show the sender's own message immediately without
      // waiting for the realtime INSERT to round-trip. The realtime handler
      // dedupes on processedMessagesRef + messages.some(id===) so the echo
      // is a no-op when it arrives.
      const row = inserted as any;
      if (row?.id) {
        processedMessagesRef.current.add(row.id);
        setConversations(prev => prev.map(conv => {
          if (conv.id !== conversationId) return conv;
          if (conv.messages.some(m => m.id === row.id)) return conv;
          const messageToAdd: Message = {
            id: row.id,
            sender_id: row.sender_id,
            content: row.content,
            created_at: row.created_at,
            isMe: true,
            read_at: row.read_at ?? null,
          };
          return {
            ...conv,
            messages: [...conv.messages, messageToAdd],
            lastMessage: row.content,
          };
        }));
      }



    } catch (error: any) {
      // P1: structured log + user-visible toast so silent failures stop being silent.
      console.error('[sendMessage] insert failed', {
        conversationId,
        code: error?.code,
        status: error?.status,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
      });
      const isBlocked = /Messaging is unavailable between these users/i.test(
        `${error?.message ?? ''} ${error?.details ?? ''}`,
      );
      toast.error(isBlocked ? 'Messaging unavailable' : "Couldn't send message", {
        description: isBlocked
          ? 'Messaging is unavailable between these users.'
          : error?.message || 'Please try again.',
      });
    }
  };

  const openAtlasChat = async () => {
    if (!user) return;

    console.log('[ChatContext] openAtlasChat called');

    // If Atlas conversation already exists, just ensure it's visible
    if (atlasConversation) {
      console.log('[ChatContext] Atlas conversation exists, unminimizing');
      // Update to ensure it's not minimized
      setAtlasConversation({
        ...atlasConversation,
        isMinimized: false
      });
      return;
    }

    try {
      // Load Atlas conversation from localStorage
      const storedAtlas = localStorage.getItem(`atlas-conversation-${user.id}`);
      let messages: Message[] = [];

      if (storedAtlas) {
        const parsed = JSON.parse(storedAtlas);
        messages = trimAtlasMessages(parsed.messages || []);
      }

      // If no messages exist, add welcome message
      if (messages.length === 0) {
        messages.push({
          id: 'welcome-msg',
          sender_id: 'atlas',
          content: "Hello! I'm Atlas, your AI assistant. I have access to your database and can help you with loads, analytics, and insights. How can I assist you today?",
          created_at: new Date().toISOString(),
          isMe: false
        });
      }

      const newAtlasConversation: ChatConversation = {
        id: `atlas-${user.id}`,
        name: 'Atlas',
        avatar: '/placeholder.svg',
        isOnline: true,
        lastMessage: messages[messages.length - 1]?.content,
        messages,
        isMinimized: false,
        unreadCount: 0,
        isAtlas: true
      };
      
      console.log('[ChatContext] Setting new Atlas conversation with', messages.length, 'messages');
      setAtlasConversation(newAtlasConversation);
      
      // Save to localStorage
      localStorage.setItem(`atlas-conversation-${user.id}`, JSON.stringify(newAtlasConversation));
    } catch (error) {
      console.error('Error opening Atlas chat:', error);
    }
  };

  const forceOpenAtlas = () => {
    console.log('[ChatContext] forceOpenAtlas called, incrementing signal');
    setAtlasOpenSignal(prev => prev + 1);
  };

  const sendAtlasMessage = async (text: string) => {
    if (!user) return;

    try {
      // Add user message
      addAtlasMessage(text, true);

      // Add cycling loading messages
      const thinkingMessages = [
        '🤔 Thinking...',
        '📊 Analyzing...',
        '🔍 Researching...',
        '💡 Formulating response...'
      ];

      let messageIndex = 0;
      addAtlasMessage(thinkingMessages[0]);

      const thinkingInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % thinkingMessages.length;
        addAtlasMessage(thinkingMessages[messageIndex]);
      }, 2500);

      // Build message history in OpenAI format (last 5 messages excluding loading messages)
      const recentMessages = atlasConversation?.messages
        ?.filter(m => !/^[🤔📊🔍💡]/.test(m.content)) // Exclude loading messages
        ?.slice(-5)
        ?.map(m => ({
          role: m.sender_id === 'atlas' ? 'assistant' : 'user',
          content: m.content
        })) || [];

      // Add current message to history
      const messageHistory = [
        ...recentMessages,
        { role: 'user', content: text }
      ];

      // Extract and geocode any location mentions in the user's message
      const extractedLocations = await extractAndGeocodeLocations(text);

      // Call the edge function with user context
      const { data: analysisData, error: analysisError } = await supabase.functions.invoke('atlas-load-analysis', {
        body: { 
          type: 'question',
          currentMessage: text,
          messageHistory: messageHistory,
          extractedLocations: extractedLocations,
          userRole: profile?.user_type || 'unknown',
        }
      });

      clearInterval(thinkingInterval);
      
      // Small delay before clearing and showing final message
      await new Promise(resolve => setTimeout(resolve, 100));
      clearAtlasLoadingMessages();

      if (analysisError) {
        console.error('Error from Atlas edge fn:', analysisError);
        // supabase-js FunctionsHttpError: parse the Response body to find the real error.
        let parsedBody: any = null;
        try {
          const ctx: any = (analysisError as any).context;
          if (ctx && typeof ctx.json === 'function') {
            parsedBody = await ctx.clone().json().catch(() => null);
          }
        } catch { /* ignore */ }

        const status: number | undefined = (analysisError as any).context?.status ?? (analysisError as any).status;
        const isCreditsError =
          status === 402 ||
          parsedBody?.error === 'insufficient_credits' ||
          /402/.test(String(analysisError.message || ''));

        if (isCreditsError) {
          addAtlasMessage("You're out of Atlas credits. Top up in Company Settings → Billing to keep asking.");
          setAtlasCreditsExhausted(true);
          return;
        }

        const detail = parsedBody?.error || parsedBody?.output || analysisError.message || 'Unknown error';
        addAtlasMessage(`Sorry, I couldn't answer that. (${detail}) Please try again.`);
        return;
      }

      // Check for insufficient_credits in the response body (edge fn returned 402)
      if (analysisData?.error === 'insufficient_credits') {
        addAtlasMessage("You're out of Atlas credits. Top up in Company Settings → Billing to keep asking.");
        setAtlasCreditsExhausted(true);
        return;
      }

      // Add the AI response
      const aiResponse = analysisData?.output || analysisData?.response || 'I received your message but got an unexpected response format.';
      addAtlasMessage(aiResponse);

    } catch (error) {
      console.error('Error sending Atlas message:', error);
      clearAtlasLoadingMessages();
      addAtlasMessage('Sorry, I encountered an error. Please try again.');
    }
  };

  const addAtlasMessage = async (content: string, isFromUser: boolean = false) => {
    if (!user) return;

    // Check if this is a loading message (contains emoji indicators)
    const isLoadingMessage = /^[🤔📊🔍💡]/.test(content);

    console.log('[ChatContext] addAtlasMessage:', { 
      content: content.substring(0, 50), 
      isLoadingMessage, 
      isFromUser 
    });

    const tempMessage: Message = {
      id: `atlas-msg-${Date.now()}`,
      sender_id: isFromUser ? user.id : 'atlas',
      content,
      created_at: new Date().toISOString(),
      isMe: isFromUser
    };

    setAtlasConversation(prev => {
      // Initialize conversation if it doesn't exist yet
      const baseConv: ChatConversation = prev ?? {
        id: `atlas-${user.id}`,
        name: 'Atlas',
        avatar: '/placeholder.svg',
        isOnline: true,
        lastMessage: undefined,
        messages: [],
        isMinimized: false,
        unreadCount: 0,
        isAtlas: true
      };

      // If it's a loading message, replace the last loading message instead of adding new one
      if (isLoadingMessage) {
        const lastMessage = baseConv.messages[baseConv.messages.length - 1];
        const lastIsLoading = lastMessage && /^[🤔📊🔍💡]/.test(lastMessage.content);
        
        if (lastIsLoading) {
          // Replace the last loading message
          const updated = {
            ...baseConv,
            messages: [
              ...baseConv.messages.slice(0, -1),
              { ...tempMessage, id: lastMessage.id } // Keep same ID for smooth update
            ]
          };
          console.log('[ChatContext] Replaced loading message, total messages:', updated.messages.length);
          return updated;
        }
      }

      // Otherwise, append the new message
      const updated = {
        ...baseConv,
        messages: [...baseConv.messages, tempMessage],
        lastMessage: isLoadingMessage ? baseConv.lastMessage : content
      };

      console.log('[ChatContext] Added new message, total messages:', updated.messages.length);

      // Only persist non-loading messages, trimmed to cap
      if (!isLoadingMessage) {
        const toSave = { ...updated, messages: trimAtlasMessages(updated.messages) };
        localStorage.setItem(`atlas-conversation-${user.id}`, JSON.stringify(toSave));
      }

      return updated;
    });
  };

  const clearAtlasLoadingMessages = () => {
    if (!user) return;
    
    console.log('[ChatContext] clearAtlasLoadingMessages called');
    
    setAtlasConversation(prev => {
      if (!prev) return null;
      
      const loadingCount = prev.messages.filter(msg => /^[🤔📊🔍💡]/.test(msg.content)).length;
      
      const filtered = {
        ...prev,
        messages: prev.messages.filter(msg => !/^[🤔📊🔍💡]/.test(msg.content))
      };
      
      console.log('[ChatContext] Cleared', loadingCount, 'loading messages, remaining:', filtered.messages.length);
      
      // Save to localStorage after clearing loading messages, trimmed
      const toSave = { ...filtered, messages: trimAtlasMessages(filtered.messages) };
      localStorage.setItem(`atlas-conversation-${user.id}`, JSON.stringify(toSave));
      
      return filtered;
    });
  };

  const unreadTotal = conversations
    .filter(c => !c.isAtlas && !hiddenConversations.includes(c.id))
    .reduce((n, c) => n + (c.unreadCount || 0), 0);

  const value: ChatContextType = {
    conversations,
    openChats,
    hiddenConversations,
    notification,
    atlasConversation,
    atlasOpenSignal,
    atlasCreditsExhausted,
    atlasDockOpen,
    openAtlasDock: () => { setAtlasDockOpen(true); openAtlasChat(); },
    closeAtlasDock: () => setAtlasDockOpen(false),
    openChatWithUser,
    toggleChat,
    closeChat,
    hideConversation,
    sendMessage,
    openAtlasChat,
    forceOpenAtlas,
    sendAtlasMessage,
    addAtlasMessage,
    clearAtlasLoadingMessages,
    setNotification,
    setOpenChats,
    setConversations,
    setHiddenConversations,
    messengerOpen,
    unreadTotal,
    toggleMessenger: () => setMessengerOpen(v => !v),
    openMessenger: () => setMessengerOpen(true),
    closeMessenger: () => setMessengerOpen(false),
    openConversationFromNotification: (conversationId: string) => {
      setMessengerOpen(true);
      openConversationFromNotifRef.current?.(conversationId);
    },
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  return useContext(ChatContext);
};